import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Trash2, Plus, GripVertical, Edit, Check, Star, ChevronDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PAGE_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'regular', label: 'Regular Pages' },
  { value: 'expo', label: 'Expo Pages' },
  { value: 'festival', label: 'Festival Pages' },
];

// Sortable Menu Item Component
function SortableMenuItem({ item, editingItem, setEditingItem, handleUpdateItem, handleDeleteItem }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card className="p-4" data-testid={`menu-item-${item.id}`}>
        {editingItem?.id === item.id ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                value={editingItem.label}
                onChange={(e) =>
                  setEditingItem({ ...editingItem, label: e.target.value })
                }
                placeholder="Label"
              />
              <Select
                value={editingItem.type}
                onValueChange={(value) =>
                  setEditingItem({ ...editingItem, type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal Page</SelectItem>
                  <SelectItem value="anchor">Anchor (#section)</SelectItem>
                  <SelectItem value="custom">Custom URL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input
              value={editingItem.target}
              onChange={(e) =>
                setEditingItem({ ...editingItem, target: e.target.value })
              }
              placeholder="Target (URL, /page-slug, or #anchor)"
            />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editingItem.is_visible}
                  onChange={(e) =>
                    setEditingItem({ ...editingItem, is_visible: e.target.checked })
                  }
                  className="rounded"
                />
                Visible
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editingItem.open_in_new_tab}
                  onChange={(e) =>
                    setEditingItem({ ...editingItem, open_in_new_tab: e.target.checked })
                  }
                  className="rounded"
                />
                Open in new tab
              </label>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => handleUpdateItem(item.id, editingItem)}
                className="bg-black hover:bg-gray-800"
              >
                <Check size={16} className="mr-2" />
                Save
              </Button>
              <Button
                onClick={() => setEditingItem(null)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div {...listeners} className="cursor-grab active:cursor-grabbing">
                <GripVertical className="text-gray-400 hover:text-gray-600" size={20} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900">{item.label}</p>
                  {!item.is_visible && (
                    <Badge variant="secondary" className="text-xs">Hidden</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {item.type === 'internal' ? 'Page: ' : item.type === 'anchor' ? 'Anchor: ' : 'URL: '}
                  {item.target}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setEditingItem(item)}
                variant="ghost"
                size="sm"
              >
                <Edit size={18} />
              </Button>
              <Button
                onClick={() => handleDeleteItem(item.id)}
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 size={18} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

const MenuManager = () => {
  const [menus, setMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [isMenuDialogOpen, setIsMenuDialogOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const { toast } = useToast();

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchMenus();
  }, []);

  useEffect(() => {
    if (selectedMenu) {
      fetchMenuItems(selectedMenu.id);
    }
  }, [selectedMenu]);

  const fetchMenus = async () => {
    try {
      const response = await api.get('/menus');
      setMenus(response.data);
      // Select the default menu or the first one
      const defaultMenu = response.data.find(m => m.is_default) || response.data[0];
      if (defaultMenu) {
        setSelectedMenu(defaultMenu);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch menus',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async (menuId) => {
    try {
      const response = await api.get(`/menu?menu_id=${menuId}`);
      setMenuItems(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch menu items',
        variant: 'destructive',
      });
    }
  };

  const handleCreateMenu = async (formData) => {
    try {
      const response = await api.post('/admin/menus', formData);
      toast({ title: 'Success', description: 'Menu created' });
      setIsMenuDialogOpen(false);
      setEditingMenu(null);
      fetchMenus();
      setSelectedMenu(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create menu',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateMenu = async (formData) => {
    try {
      await api.put(`/admin/menus/${editingMenu.id}`, formData);
      toast({ title: 'Success', description: 'Menu updated' });
      setIsMenuDialogOpen(false);
      setEditingMenu(null);
      fetchMenus();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update menu',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteMenu = async (menuId) => {
    if (!window.confirm('Are you sure you want to delete this menu and all its items?')) return;

    try {
      await api.delete(`/admin/menus/${menuId}`);
      toast({ title: 'Success', description: 'Menu deleted' });
      fetchMenus();
      setSelectedMenu(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete menu',
        variant: 'destructive',
      });
    }
  };

  const handleCreateItem = async () => {
    if (!selectedMenu) return;
    
    const newItem = {
      label: 'New Item',
      type: 'anchor',
      target: '#',
      open_in_new_tab: false,
      is_visible: true,
      order: menuItems.length,
      menu_id: selectedMenu.id,
    };

    try {
      await api.post('/admin/menu', newItem);
      toast({ title: 'Success', description: 'Menu item created' });
      fetchMenuItems(selectedMenu.id);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create menu item',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateItem = async (id, data) => {
    try {
      await api.put(`/admin/menu/${id}`, data);
      toast({ title: 'Success', description: 'Menu item updated' });
      setEditingItem(null);
      fetchMenuItems(selectedMenu.id);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update menu item',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) return;

    try {
      await api.delete(`/admin/menu/${id}`);
      toast({ title: 'Success', description: 'Menu item deleted' });
      fetchMenuItems(selectedMenu.id);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete menu item',
        variant: 'destructive',
      });
    }
  };

  // Handle drag end for reordering menu items
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = menuItems.findIndex((item) => item.id === active.id);
      const newIndex = menuItems.findIndex((item) => item.id === over.id);
      
      // Optimistically update UI
      const newItems = arrayMove(menuItems, oldIndex, newIndex);
      setMenuItems(newItems);

      // Persist to backend
      try {
        const reorderData = newItems.map((item, index) => ({
          id: item.id,
          order: index,
        }));
        await api.post('/admin/menu/reorder', { items: reorderData });
        toast({ title: 'Success', description: 'Menu items reordered' });
      } catch (error) {
        // Revert on error
        fetchMenuItems(selectedMenu.id);
        toast({
          title: 'Error',
          description: 'Failed to reorder menu items',
          variant: 'destructive',
        });
      }
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Menu Manager</h1>
          <p className="text-gray-600 mt-2">Manage multiple navigation menus</p>
        </div>
        <Button 
          onClick={() => { setEditingMenu(null); setIsMenuDialogOpen(true); }} 
          className="bg-black hover:bg-gray-800"
        >
          <Plus size={20} className="mr-2" />
          New Menu
        </Button>
      </div>

      {/* Menu Selector */}
      <Card className="p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Select Menu:</label>
            <Select 
              value={selectedMenu?.id || ''} 
              onValueChange={(id) => setSelectedMenu(menus.find(m => m.id === id))}
            >
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select a menu" />
              </SelectTrigger>
              <SelectContent>
                {menus.map((menu) => (
                  <SelectItem key={menu.id} value={menu.id}>
                    <div className="flex items-center gap-2">
                      {menu.name}
                      {menu.is_default && <Star size={14} className="text-yellow-500" />}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {selectedMenu && (
            <div className="flex items-center gap-2">
              {selectedMenu.is_default && (
                <Badge className="bg-yellow-100 text-yellow-800">Default</Badge>
              )}
              {selectedMenu.page_type_default && (
                <Badge className="bg-blue-100 text-blue-800">
                  Default for {selectedMenu.page_type_default} pages
                </Badge>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => { setEditingMenu(selectedMenu); setIsMenuDialogOpen(true); }}
              >
                <Edit size={16} className="mr-2" />
                Edit Menu
              </Button>
              {!selectedMenu.is_default && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDeleteMenu(selectedMenu.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Menu Items */}
      {selectedMenu ? (
        <>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              {selectedMenu.name} Items ({menuItems.length})
            </h2>
            <Button onClick={handleCreateItem} variant="outline">
              <Plus size={18} className="mr-2" />
              Add Item
            </Button>
          </div>

          <div className="space-y-3">
            {menuItems.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-gray-500 mb-4">No items in this menu</p>
                <Button onClick={handleCreateItem} variant="outline">
                  <Plus size={18} className="mr-2" />
                  Add First Item
                </Button>
              </Card>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={menuItems.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {menuItems.map((item) => (
                    <SortableMenuItem
                      key={item.id}
                      item={item}
                      editingItem={editingItem}
                      setEditingItem={setEditingItem}
                      handleUpdateItem={handleUpdateItem}
                      handleDeleteItem={handleDeleteItem}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </>
      ) : (
        <Card className="p-12 text-center">
          <p className="text-xl text-gray-500 mb-4">No menus found</p>
          <Button 
            onClick={() => { setEditingMenu(null); setIsMenuDialogOpen(true); }} 
            className="bg-black hover:bg-gray-800"
          >
            <Plus size={20} className="mr-2" />
            Create Your First Menu
          </Button>
        </Card>
      )}

      {/* Menu Dialog */}
      <Dialog open={isMenuDialogOpen} onOpenChange={setIsMenuDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingMenu ? 'Edit Menu' : 'Create New Menu'}
            </DialogTitle>
          </DialogHeader>
          <MenuForm 
            menu={editingMenu} 
            onSave={editingMenu ? handleUpdateMenu : handleCreateMenu}
            onCancel={() => { setIsMenuDialogOpen(false); setEditingMenu(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Menu Form Component
function MenuForm({ menu, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: menu?.name || '',
    is_default: menu?.is_default || false,
    page_type_default: menu?.page_type_default || 'none',
    cta_text: menu?.cta_text || 'Subscribe',
    cta_link: menu?.cta_link || '#newsletter',
  });

  const handleSubmit = () => {
    if (!formData.name.trim()) return;
    onSave({
      ...formData,
      page_type_default: formData.page_type_default === 'none' ? null : formData.page_type_default,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Menu Name</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Main Menu, Expo Menu"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Default for Page Type
        </label>
        <Select
          value={formData.page_type_default || 'none'}
          onValueChange={(value) => setFormData({ ...formData, page_type_default: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select page type (optional)" />
          </SelectTrigger>
          <SelectContent>
            {PAGE_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 mt-1">
          This menu will automatically be used for pages of this type
        </p>
      </div>

      <div className="border-t pt-4 mt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Header CTA Button</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
            <Input
              value={formData.cta_text}
              onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
              placeholder="e.g., Subscribe, Register, Attend"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Button Link</label>
            <Input
              value={formData.cta_link}
              onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
              placeholder="e.g., #newsletter, /register"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          This button appears in the navigation header for pages using this menu
        </p>
      </div>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.is_default}
          onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
          className="rounded"
        />
        <span className="text-sm">Set as global default menu</span>
      </label>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} className="bg-black hover:bg-gray-800">
          {menu ? 'Update Menu' : 'Create Menu'}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default MenuManager;
